/**
 * SanPlumb — Componente raíz (Fase 3c)
 *
 * Integra el shell (TopBar + StatusBar + modales) con las pantallas reales
 * Dashboard, Hunter y Dotación. El Visor 3D sigue como placeholder hasta 3d.
 */

import { useEffect, useState } from 'react';
import './App.css';

import type {
  Screen,
  DoneState,
  LayerState,
  UsoMode,
  CountsState,
  CameraView,
  RightTab,
  SelectedElement,
  BrandState,
  RoomDims,
} from './types/ui';
import type { RoutingStrategy } from './types';
import { DEFAULT_ROUTING, DEFAULT_BRANDS, routingOption } from './constants/ui';
import { initialCounts, calcUG } from './constants/aparatosUI';
import { detectTipologia, countsForTipologia, type TipologiaCode } from './constants/tipologias';
import { interpolateHunter } from './engine/hunter';
import { SESSION_KEYS } from './engine/sessionState';
import { useSessionState } from './hooks/useSessionState';
import { TopBar } from './components/layout/TopBar';
import { StatusBar } from './components/layout/StatusBar';
import { Dashboard } from './components/steps/Dashboard';
import { Hunter } from './components/steps/Hunter';
import { Dotacion } from './components/steps/Dotacion';
import { ViewerScreen } from './components/viewer/ViewerScreen';
import { IngestaModal } from './components/steps/IngestaModal';
import { exportBOMtoXLSX } from './modules/exportBOM';
import { systemForRouting } from './engine/three/generateSystem';
import { BootLogo } from './components/Brand';

// La animación isométrica del logo dura ~1.6 s (líneas + pop de círculos).
// El splash espera un poco más para que el dibujo termine antes de pasar.
const BOOT_MS = 1900;

function App(): JSX.Element {
  const [screen, setScreen] = useState<Screen>('boot');
  const [prevScreen, setPrevScreen] = useState<Screen | null>(null);
  const [done, setDone] = useState<DoneState>({
    dash: true,
    hunter: false,
    ingesta: false,
    viewer: false,
  });

  const [counts, setCounts] = useSessionState<CountsState>(
    SESSION_KEYS.HUNTER_COUNTS,
    initialCounts(),
  );
  const [mode] = useState<UsoMode>('priv');

  // Tipología §XI. Si el usuario no ha elegido manualmente, se infiere de
  // counts en cada render (selector "auto"). El override del usuario se
  // guarda en sesión para que persista entre pantallas.
  const [tipologiaOverride, setTipologiaOverride] = useSessionState<TipologiaCode | null>(
    SESSION_KEYS.TIPOLOGIA,
    null,
  );
  const tipologia: TipologiaCode = tipologiaOverride ?? detectTipologia(counts);

  /**
   * Cambio de tipología desde el selector: además de fijar el override,
   * resetea los counts del Hunter al template de esa tipología (los +/-
   * del Hunter siguen disponibles para ajustar). Si el usuario pone null,
   * se vuelve a auto-detección sin tocar los counts actuales.
   */
  function handleSetTipologia(code: TipologiaCode | null): void {
    setTipologiaOverride(code);
    if (code) setCounts(countsForTipologia(code));
  }

  // Dimensiones del recinto declaradas por el usuario en la ingesta.
  // null = usar las sugeridas por la tipología §XI. Cuando el usuario
  // confirma medidas en el modal, los muros virtuales se construyen
  // contra estos valores reales.
  const [roomDims, setRoomDims] = useSessionState<RoomDims | null>(
    SESSION_KEYS.ROOM_DIMS,
    null,
  );

  // Estrategia de routing: solo `muro_dual` (Estrategia A) sigue activa en el
  // MVP. Las estrategias B (mixta) y C (piso_dual) quedan en el engine para
  // tests/futuras iteraciones, pero el UI no las expone y no se rota.
  const [routing] = useSessionState<RoutingStrategy>(SESSION_KEYS.ROUTING, DEFAULT_ROUTING);

  const [layers, setLayers] = useSessionState<LayerState>(SESSION_KEYS.LAYERS, {
    sanitarios: true,
    planos: true,
    muros: true,
    labelsNombre: true,
    labelsMedidas: false,
    showAF: true,
    showAC: true,
  });

  // Estado del visor 3D
  const [view, setView] = useState<CameraView>('ISO');
  const [tab, setTab] = useState<RightTab>('BOM');
  const [selected, setSelected] = useState<SelectedElement>({ kind: 'none' });
  const [brand, setBrand] = useSessionState<BrandState>(SESSION_KEYS.BRAND, DEFAULT_BRANDS);
  const [animKey, setAnimKey] = useState<number>(0);
  const [autoRotate, setAutoRotate] = useSessionState<boolean>(
    SESSION_KEYS.AUTO_ROTATE,
    true,
  );

  const [ingestaOpen, setIngestaOpen] = useState<boolean>(false);

  useEffect(() => {
    if (screen !== 'boot') return;
    const t = setTimeout(() => setScreen('dash'), BOOT_MS);
    return () => clearTimeout(t);
  }, [screen]);

  function goto(s: Screen): void {
    if (s !== screen) setPrevScreen(screen);
    setScreen(s);
    if (s === 'dash' || s === 'hunter' || s === 'viewer') {
      setDone((prev) => ({ ...prev, [s]: true }));
    }
    if (s === 'viewer') setAnimKey((k) => k + 1);
  }

  function openIngesta(): void {
    setIngestaOpen(true);
    setDone((prev) => ({ ...prev, ingesta: true }));
  }

  function toggleLayer(k: keyof LayerState): void {
    setLayers((s) => ({ ...s, [k]: !s[k] }));
  }

  function handleExport(): void {
    // xlsx-js-style se carga dinámicamente; cualquier error de IO se loggea.
    const sys = systemForRouting(routing, counts, tipologia, mode, roomDims ?? undefined);
    void exportBOMtoXLSX(sys, 'sanplumb-bom.xlsx', brand).catch((e) => {
      console.error('Export XLSX falló:', e);
    });
  }

  function confirmIngesta(dims: RoomDims): void {
    setRoomDims(dims);
    setIngestaOpen(false);
    goto('viewer');
  }

  if (screen === 'boot') {
    return (
      <div className="cover-overlay">
        <div className="boot-stack">
          <BootLogo />
          <div className="boot-tagline">ISOMÉTRICO 3D · IS.010 · BOM AUTOMÁTICO</div>
          <div className="boot-bar">
            <div className="boot-bar-fill" />
          </div>
        </div>
      </div>
    );
  }

  const SCREEN_ORDER: Screen[] = ['dash', 'hunter', 'viewer', 'dotacion'];
  const dir: 'in' | 'forward' | 'back' = (() => {
    if (!prevScreen) return 'in';
    const a = SCREEN_ORDER.indexOf(prevScreen);
    const b = SCREEN_ORDER.indexOf(screen);
    if (a < 0 || b < 0) return 'in';
    return b > a ? 'forward' : 'back';
  })();

  const routingLabel = routingOption(routing).label;

  const { af, ac, ug } = calcUG(counts, mode);
  const q = interpolateHunter(ug);

  return (
    <div className="app">
      <TopBar
        screen={screen}
        done={done}
        onNavigate={goto}
        onOpenIngesta={openIngesta}
        onExport={handleExport}
      />

      <div className={`screen-host dir-${dir}`} key={screen}>
        {screen === 'dash' && <Dashboard onNavigate={goto} onOpenIngesta={openIngesta} />}

        {screen === 'hunter' && (
          <Hunter
            counts={counts}
            setCounts={setCounts}
            mode={mode}
            tipologia={tipologia}
            tipologiaOverride={tipologiaOverride}
            onSetTipologia={handleSetTipologia}
            onOpenIngesta={openIngesta}
          />
        )}

        {screen === 'dotacion' && <Dotacion />}

        {screen === 'viewer' && (
          <ViewerScreen
            routing={routing}
            tipologia={tipologia}
            roomDims={roomDims}
            layers={layers}
            view={view}
            animKey={animKey}
            tab={tab}
            selected={selected}
            counts={counts}
            mode={mode}
            brand={brand}
            autoRotate={autoRotate}
            onToggleAutoRotate={() => setAutoRotate((v) => !v)}
            onSetView={setView}
            onToggleLayer={toggleLayer}
            onSetTab={setTab}
            onSelect={setSelected}
            setBrand={setBrand}
          />
        )}
      </div>

      <StatusBar
        q={q}
        ug={ug}
        precise={false}
        showAC={ac > 0}
        tramosAF={Math.max(1, Math.round(af))}
        tramosAC={Math.max(0, Math.round(ac))}
        accesorios={14}
        brand="PAVCO"
        routingLabel={routingLabel}
      />

      {ingestaOpen && (
        <IngestaModal
          tipologia={tipologia}
          initialDims={roomDims}
          onClose={() => setIngestaOpen(false)}
          onConfirm={confirmIngesta}
        />
      )}
    </div>
  );
}

export default App;
